import styled from 'styled-components';
import {InfoAlert} from '../../../components/Alerts/Alerts';
import {Section, SectionHeader, SubSectionHeader} from '../../../components/Form/FormStyles';
import {EditedProject, visibleFundingParticipations} from '../EditedProject';
import {CommunityDBHTreesDistributedControl} from '../MultipleProjectTypes/CommunityDBHTreesDistributedControl';
import {CommunityEventDatesControl} from '../MultipleProjectTypes/CommunityEventDatesControl';
import {CommunityTreesDistributedControl} from '../MultipleProjectTypes/CommunityTreesDistributedControl';
import {CommunityTreesPlantedControl} from '../MultipleProjectTypes/CommunityTreesPlantedControl';
import {CommunityTreesPlantedDBHControl} from '../MultipleProjectTypes/CommunityTreesPlantedDBHControl';
import {D365EventNumberControl} from './D365EventNumberControl';
import {FundingPartnersControl} from '../MultipleProjectTypes/FundingPartnersControl';
import {ImpactStoryCompletedControl} from '../MultipleProjectTypes/ImpactStoryCompletedControl';
import {ImpactStoryUneditedControl} from '../MultipleProjectTypes/ImpactStoryFromUnedited';
import {ImpactStoryInProgressControl} from '../MultipleProjectTypes/ImpactStoryInProgressControl';
import {ImpactStoryInProgressShortControl} from '../MultipleProjectTypes/ImpactStoryInProgressShortControl';
import {LocationControl} from '../MultipleProjectTypes/LocationControl';
import {MapControl} from '../MultipleProjectTypes/Map/MapControl';
import {NameControl} from '../MultipleProjectTypes/NameControl';
import {NumberOfCommunityVolunteers} from './NumberOfCommunityVolunteers';
import {NumberOfCorporateVolunteersControl} from './NumberOfCorporateVolunteersControl';
import {NumberOfIndirectPeopleImpactedControl} from './NumberOfIndirectPeopleImpactedControl';
import {PlantingPartnerControl} from '../MultipleProjectTypes/PlantingPartnerControl';
import {ProjectIdControl} from '../MultipleProjectTypes/ProjectIdControl';
import {ProjectStatusControl} from '../MultipleProjectTypes/ProjectStatusControl';
import {ProjectTypeControl} from '../MultipleProjectTypes/ProjectTypeControl';
import {DemoProjectStatusControl} from '../MultipleProjectTypes/DemoProjectStatusControl';
import {ReasonForPlantingControl} from '../MultipleProjectTypes/ReasonForPlantingControl';
import {ScalabilityControl} from '../MultipleProjectTypes/ScalabilityControl';
import {ImpactsControl} from '../MultipleProjectTypes/ImpactsControl';
import {ProposedProjectCostControl} from '../MultipleProjectTypes/ProposedProjectCostControl';
import {TreeSpeciesControl} from '../MultipleProjectTypes/TreeSpeciesControl';
import {ImagesSection} from '../MultipleProjectTypes/ImagesSection';
import {SiteSection} from './SiteSection';
import {D365EventCoordinationIdControl} from './D365EventCoordinationIdControl';
import {ImpactStoryCompletedShortControl} from '../MultipleProjectTypes/ImpactStoryCompletedShortControl';
import {BenefitsSection} from '../MultipleProjectTypes/BenefitsSection';

const TreeMetricsLayout = styled.div`
    display: grid;
    grid-template-columns: repeat(2, 275px);
    gap: 1rem 1rem;
`;

export function CommunityProject({
    project,
    setProjectAndValidate,
    completedProjectSetProjectAndValidate,
    newProject,
    navigationPromptActive,
    setNavigationPromptActive,
    registerSave,
    registerHasChanges,
    hasChangesUpdated,
    generateShortImpactStory,
    proposalMode,
    reloadProject
}: {
    project: EditedProject;
    setProjectAndValidate: (project: EditedProject) => void;
    completedProjectSetProjectAndValidate: (project: EditedProject) => void;
    newProject?: boolean;
    navigationPromptActive: boolean;
    setNavigationPromptActive: (navigationPromptActive: boolean) => void;
    registerSave: (f: (projectId: number) => void) => void;
    registerHasChanges: (f: () => boolean) => void;
    hasChangesUpdated: () => void;
    generateShortImpactStory: (originalText: string) => Promise<string>;
    proposalMode?: boolean;
    reloadProject: () => void;
}) {
    return (
        <>
            {!proposalMode && (
                <Section>
                    {project.ControlledByAutomation && (
                        <InfoAlert style={{width: '100%', maxWidth: '1100px'}}>
                            This project is being automatically updated from D365. Most fields are
                            read-only on this page. To update them, go to the{' '}
                            <a
                                href={`https://arbordayfarm.crm.dynamics.com/main.aspx?appid=aaf69a85-59da-4da2-9c1b-219a888ed0e8&forceUCI=1&pagetype=entityrecord&etn=stn_eventcoordination&id=${project.D365_eventcoordinationid.current}`}
                                target="_blank"
                                rel="noopener noreferrer">
                                D365 event coordination
                            </a>
                            .
                        </InfoAlert>
                    )}
                    <ProjectStatusControl
                        project={project}
                        setProjectAndValidate={setProjectAndValidate}
                        completedProjectSetProjectAndValidate={completedProjectSetProjectAndValidate}
                    />
                </Section>
            )}

            <Section>
                <SectionHeader>{proposalMode ? 'What is the proposal?' : 'What is the project?'}</SectionHeader>
                {!proposalMode && <ProjectIdControl project={project} />}
                {!proposalMode && (
                    <DemoProjectStatusControl
                        project={project}
                        setProjectAndValidate={setProjectAndValidate}
                    />
                )}
                {!proposalMode && (
                    <ProjectTypeControl
                        project={project}
                        setProjectAndValidate={setProjectAndValidate}
                    />
                )}
                {!proposalMode && newProject && (
                    <D365EventNumberControl
                        project={project}
                        setProjectAndValidate={setProjectAndValidate}
                    />
                )}
                {!proposalMode && (
                    <D365EventCoordinationIdControl
                        project={project}
                        setProjectAndValidate={setProjectAndValidate}
                    />
                )}
                <NameControl project={project} setProjectAndValidate={setProjectAndValidate} />
                {proposalMode && (
                    <ScalabilityControl
                        project={project}
                        setProjectAndValidate={setProjectAndValidate}
                    />
                )}
            </Section>

            <Section>
                <SectionHeader>{proposalMode ? 'Where is the proposal?' : 'Where is the project?'}</SectionHeader>
                <LocationControl project={project} setProjectAndValidate={setProjectAndValidate} />
                {!proposalMode && (
                    <SiteSection project={project} setProjectAndValidate={setProjectAndValidate} />
                )}
                <MapControl
                    project={project}
                    setProjectAndValidate={setProjectAndValidate}
                    registerSave={registerSave}
                    registerHasChanges={registerHasChanges}
                    hasChangesUpdated={hasChangesUpdated}
                    navigationPromptActive={navigationPromptActive}
                    setNavigationPromptActive={setNavigationPromptActive}
                    proposalMode={proposalMode}
                />
            </Section>

            <Section>
                <SectionHeader>{proposalMode ? 'When is the proposal?' : 'When is the project?'}</SectionHeader>
                <CommunityEventDatesControl
                    project={project}
                    setProjectAndValidate={setProjectAndValidate}
                />
            </Section>

            <Section>
                <SectionHeader>{proposalMode ? 'Why is the proposal being done?' : 'Why is the project being done?'}</SectionHeader>
                <ReasonForPlantingControl
                    project={project}
                    setProjectAndValidate={setProjectAndValidate}
                />
                {proposalMode && (
                    <ImpactsControl project={project} setProjectAndValidate={setProjectAndValidate} />
                )}
                {!proposalMode && (
                    <ImpactStoryUneditedControl
                        project={project}
                        setProjectAndValidate={setProjectAndValidate}
                    />
                )}
                <ImpactStoryInProgressControl
                    project={project}
                    setProjectAndValidate={setProjectAndValidate}
                    label={proposalMode ? 'Proposal Impact story' : undefined}
                />
                {!proposalMode && (
                    <ImpactStoryInProgressShortControl
                        project={project}
                        setProjectAndValidate={setProjectAndValidate}
                        generateShortImpactStory={generateShortImpactStory}
                    />
                )}
                {!proposalMode && (
                    <ImpactStoryCompletedControl
                        project={project}
                        setProjectAndValidate={setProjectAndValidate}
                    />
                )}
                {!proposalMode && (
                    <ImpactStoryCompletedShortControl
                        project={project}
                        setProjectAndValidate={setProjectAndValidate}
                        generateShortImpactStory={generateShortImpactStory}
                    />
                )}
            </Section>

            <Section>
                <SectionHeader>{proposalMode ? 'Who is participating in the proposal?' : 'Who is participating in the project?'}</SectionHeader>
                <PlantingPartnerControl
                    project={project}
                    setProjectAndValidate={setProjectAndValidate}
                />
                {proposalMode && (
                    <ProposedProjectCostControl
                        project={project}
                        setProjectAndValidate={setProjectAndValidate}
                    />
                )}
                <FundingPartnersControl
                    participations={visibleFundingParticipations(project)}
                    projectId={project.Id}
                    projectType={project.ProjectType.current}
                    onParticipationChange={reloadProject}
                    proposalMode={proposalMode}
                />
                <SubSectionHeader>People Impacted</SubSectionHeader>
                <NumberOfCorporateVolunteersControl
                    project={project}
                    setProjectAndValidate={setProjectAndValidate}
                />
                <NumberOfCommunityVolunteers
                    project={project}
                    setProjectAndValidate={setProjectAndValidate}
                />
                <NumberOfIndirectPeopleImpactedControl
                    project={project}
                    setProjectAndValidate={setProjectAndValidate}
                />
            </Section>

            <Section>
                <SectionHeader>What is being planted?</SectionHeader>
                <TreeMetricsLayout>
                    <CommunityTreesPlantedControl
                        project={project}
                        setProjectAndValidate={setProjectAndValidate}
                    />
                    <CommunityTreesDistributedControl
                        project={project}
                        setProjectAndValidate={setProjectAndValidate}
                    />
                    {!proposalMode && (
                        <CommunityTreesPlantedDBHControl
                            project={project}
                            setProjectAndValidate={setProjectAndValidate}
                        />
                    )}
                    {!proposalMode && (
                        <CommunityDBHTreesDistributedControl
                            project={project}
                            setProjectAndValidate={setProjectAndValidate}
                        />
                    )}
                </TreeMetricsLayout>
                {!proposalMode && (
                    <TreeSpeciesControl
                        project={project}
                        setProjectAndValidate={setProjectAndValidate}
                    />
                )}
            </Section>

            {!proposalMode && <BenefitsSection project={project} />}

            {!proposalMode && (
                <ImagesSection
                    newProject={newProject}
                    project={project}
                    setProjectAndValidate={setProjectAndValidate}
                />
            )}
        </>
    );
}
